import type { CollectibleInstance } from '../collectibles/CollectibleInstance';
import type { CollectibleMagnet } from '../collectibles/CollectibleMagnet';
import type { CollectibleManager } from '../collectibles/CollectibleManager';
import { CollectibleEffect } from '../collectibles/CollectibleDefinition';
import { CollectibleType } from '../collectibles/CollectibleType';
import type { TurboBoost } from '../collectibles/TurboBoost';
import type { CharacterPerks } from '../config/character.config';
import type { PlayerConfig } from '../config/player.config';
import type { WorldConfig } from '../config/world.config';
import type { HazardInstance } from '../hazards/HazardInstance';
import type { HazardManager } from '../hazards/HazardManager';
import type { SafeGround } from '../level/terrain/TerrainQuery';
import type { RunnerBody } from '../level/TrackManager';
import type { TurnController } from '../level/TurnController';
import type { ObstacleInstance } from '../obstacles/ObstacleInstance';
import type { ObstacleManager } from '../obstacles/ObstacleManager';
import { ContactKind, type CollisionSystem } from '../physics/CollisionSystem';
import { TERRAIN_SOURCE_ID, TerrainContactKind, type PlayerController, type TerrainContact } from '../player/PlayerController';
import { PlayerState } from '../player/PlayerState';
import type { PursuitModel } from '../pursuers/PursuitModel';
import type { ScoreManager } from '../scoring/ScoreManager';
import type { EventBus } from './EventBus';
import type { DeathReason, GameEvents } from './GameEvents';

/** Where a runner who fell into a pit can be put down again (TrackManager). */
export interface FallRescue {
  findSafeGround(distance: number, lateral: number, body: RunnerBody): SafeGround | null;
}

export interface RunDirectorDeps {
  readonly player: PlayerController;
  readonly collisions: CollisionSystem;
  readonly obstacles: ObstacleManager;
  readonly collectibles: CollectibleManager;
  readonly score: ScoreManager;
  readonly pursuit: PursuitModel;
  readonly turns: TurnController;
  readonly events: EventBus<GameEvents>;
  readonly config: PlayerConfig;
  readonly world: WorldConfig;
  /** Without it a fall always ends the run. */
  readonly rescue?: FallRescue;
  /** Moving hazards – contacts come from the CollisionSystem. */
  readonly hazards?: HazardManager;
  /** Bottle magnet (character perk / power-up). Without it nothing is pulled in. */
  readonly magnet?: CollectibleMagnet;
  /** Turbo of the beer crate. Without it a crate only adds its bottles. */
  readonly turbo?: TurboBoost;
}

/** What the runner crashed into: an obstacle, a moving hazard or static terrain (wall, ceiling). */
interface HitSource {
  readonly id: string;
  readonly obstacle?: ObstacleInstance;
  readonly hazard?: HazardInstance;
}

/**
 * The run's rule set: turns collisions, falls and missed turns into consequences
 * (stumble, extra life, death, collecting) and decides when the run is over.
 */
export class RunDirector {
  /** Debug: ignore all damage. */
  godMode = false;

  private invulnerableTimer = 0;
  private deathTimer = 0;
  private recoverAfterHit = false;
  private over = false;
  private deathReason: DeathReason = 'HIT';
  /** Character perk: a stumble also costs a collected extra life. */
  private stumbleCostsLife = false;
  /** Scratch list: collectibles the magnet pulls in this step. */
  private readonly pulled: CollectibleInstance[] = [];

  constructor(private readonly deps: RunDirectorDeps) {}

  get isInvulnerable(): boolean {
    return this.invulnerableTimer > 0;
  }

  /** Beer-crate turbo running: invulnerable, blinking, faster. */
  get isTurbo(): boolean {
    return this.deps.turbo?.active ?? false;
  }

  get isOver(): boolean {
    return this.over;
  }

  get reason(): DeathReason {
    return this.deathReason;
  }

  reset(): void {
    this.invulnerableTimer = 0;
    this.deathTimer = 0;
    this.recoverAfterHit = false;
    this.over = false;
    this.deathReason = 'HIT';
    this.stumbleCostsLife = false;
    this.deps.turbo?.reset();
  }

  /** Rules of the selected character for this run (after reset). */
  applyPerks(perks: Pick<CharacterPerks, 'stumbleCostsExtraLife'>): void {
    this.stumbleCostsLife = perks.stumbleCostsExtraLife;
  }

  step(dt: number): void {
    const { player, collisions, score, pursuit, config, magnet, turbo } = this.deps;
    this.invulnerableTimer = Math.max(0, this.invulnerableTimer - dt);
    magnet?.update(dt);
    if (turbo?.update(dt)) {
      // The turbo ran out: a moment of the usual protection, so its end never drops the runner into an obstacle.
      this.invulnerableTimer = Math.max(this.invulnerableTimer, turbo.graceSeconds);
      this.deps.events.emit('turbo:ended', null);
    }

    if (player.state === PlayerState.Dead) {
      this.deathTimer -= dt;
      if (this.deathTimer <= 0) this.over = true;
      return;
    }
    if (player.state === PlayerState.Hit) {
      if (this.recoverAfterHit && player.timeInState >= config.hitDuration) {
        this.recoverAfterHit = false;
        player.recover();
        this.invulnerableTimer = config.invulnerabilityDuration;
      }
      return;
    }

    // Fell into a pit (or out of the world): costs a life like a missed turn.
    if (player.hasFallen || player.current.y < this.deps.world.killHeight) {
      this.fell();
      return;
    }

    const terrainContact = player.takeTerrainContact();
    if (terrainContact) {
      this.terrainHit(terrainContact);
      if (player.isIncapacitated) return;
    }

    if (this.deps.turns.update(player) === 'MISSED') {
      this.missedTurn();
      if (player.isIncapacitated) return;
    }

    const contacts = collisions.detect(player, magnet?.radius ?? 0);
    for (const item of contacts.collectibles) this.collect(item, false);
    if (magnet) for (const item of magnet.pull(contacts.runner, contacts.nearby, this.pulled)) this.collect(item, true);
    for (const contact of contacts.obstacles) {
      if (contact.kind === ContactKind.Persistent) continue;
      if (this.isProtected) {
        // Protected players smash through: the obstacle is removed instead of being passed
        // through – otherwise the camera ends up inside it (a full-screen "wall").
        if (contact.obstacle.definition.clearedOnHit) this.deps.obstacles.clearObstacle(contact.obstacle);
        continue;
      }
      const source = { id: contact.obstacle.definition.id, obstacle: contact.obstacle };
      if (contact.kind === ContactKind.Side) this.sideHit(source.id);
      else this.severeHit('HIT', source);
      if (player.isIncapacitated) break;
    }

    // Moving hazards: like obstacles, but they are never removed from play – the runner who got
    // past one (protected or with a life) is simply spared by it.
    for (const contact of contacts.hazards) {
      if (player.isIncapacitated) break;
      if (contact.kind === ContactKind.Persistent) continue;
      if (this.isProtected) {
        this.deps.hazards?.spare(contact.hazard);
        continue;
      }
      const source = { id: contact.hazard.definition.id, hazard: contact.hazard };
      if (contact.kind === ContactKind.Side) this.sideHit(source.id);
      else this.severeHit('HIT', source);
    }

    pursuit.update(dt);
    score.setDistance(player.current.distance);
  }

  /** Nothing hurts: god mode, the invulnerability after a hit or the beer-crate turbo. */
  get isProtected(): boolean {
    return this.godMode || this.isInvulnerable || this.isTurbo;
  }

  /** No turn input at a turn/T-junction. With a life (or protection) the player is turned for them. */
  private missedTurn(): void {
    const { player, score, turns, events } = this.deps;
    if (this.isProtected) {
      turns.autoTurn(player);
      return;
    }
    const lifeUsed = score.consumeExtraLife();
    events.emit('turn:missed', { lifeUsed });
    if (lifeUsed) {
      turns.autoTurn(player);
      player.hit();
      this.recoverAfterHit = true;
      return;
    }
    this.die('MISSED_TURN');
  }

  /**
   * Fell into a pit – mirrors the missed turn: with an extra life (or protection) the
   * runner is put down on safe ground past the pit and recovers like after a hit,
   * otherwise the run ends ('FELL').
   */
  private fell(): void {
    const { player, score, events, config } = this.deps;
    const body = { width: config.collider.width, standingHeight: config.collider.standingHeight, maxStepHeight: config.maxStepHeight };
    const ground = this.deps.rescue?.findSafeGround(player.current.distance, player.current.x, body) ?? null;
    if (!ground) {
      this.die('FELL');
      return;
    }
    if (this.isProtected) {
      player.respawn(ground.distance, ground.x, ground.y);
      return;
    }
    const lifeUsed = score.consumeExtraLife();
    events.emit('player:fell', { lifeUsed });
    if (!lifeUsed) {
      this.die('FELL');
      return;
    }
    // hit() pushes back by hitPushBack – the safe stretch is long enough for that.
    player.respawn(ground.distance, ground.x, ground.y);
    player.hit();
    this.recoverAfterHit = true;
  }

  /** Ran into a wall / ceiling (front) or changed lanes into one (side). */
  private terrainHit(contact: TerrainContact): void {
    if (this.isProtected) {
      this.deps.player.passTerrain(contact);
      return;
    }
    const id = TERRAIN_SOURCE_ID[contact.blocker];
    if (contact.kind === TerrainContactKind.Side) this.sideHit(id);
    else this.severeHit('HIT', { id });
  }

  private collect(item: CollectibleInstance, attracted: boolean): void {
    const { collectibles, score, events } = this.deps;
    const bottles = collectibles.collect(item, attracted);
    let livesGained = score.addBottles(bottles);
    score.recordPickup('bottles', bottles);
    if (item.definition.type === CollectibleType.ExtraLife) score.recordPickup('helmets');
    if (item.definition.type === CollectibleType.BeerCrate) score.recordPickup('crates');
    if (item.definition.effect === CollectibleEffect.AddExtraLife && score.addExtraLife()) livesGained++;
    if (item.definition.effect === CollectibleEffect.Magnet) this.deps.magnet?.activatePowerUp();
    if (item.definition.effect === CollectibleEffect.Turbo && this.deps.turbo) {
      this.deps.turbo.activate();
      events.emit('turbo:started', { seconds: this.deps.turbo.durationSeconds });
    }
    // The TIME_BONUS effect is prepared in CollectibleEffect – behaviour follows later.
    if (livesGained > 0) events.emit('score:extraLife', { extraLives: score.snapshot().extraLives });
  }

  private sideHit(sourceId: string): void {
    const { player, pursuit, score, events } = this.deps;
    player.bounceBack();
    player.stumble();
    events.emit('player:stumbled', { obstacleId: sourceId });
    if (this.stumbleCostsLife && score.consumeExtraLife()) events.emit('score:stumbleLifeLost', { extraLives: score.snapshot().extraLives });
    if (pursuit.applyStumble()) this.severeHit('CAUGHT', null);
  }

  private severeHit(reason: DeathReason, source: HitSource | null): void {
    const { player, score, pursuit, obstacles, events, config } = this.deps;
    const lifeUsed = score.consumeExtraLife();

    if (reason === 'CAUGHT') {
      events.emit('pursuit:caught', { rescued: lifeUsed });
      if (lifeUsed) {
        pursuit.rescue();
        this.invulnerableTimer = config.invulnerabilityDuration;
        return;
      }
    } else if (source) {
      events.emit('player:hit', { obstacleId: source.id, lifeUsed });
      player.hit();
      if (lifeUsed) {
        // Obstacles are cleared away; terrain stays – the invulnerable runner vaults/ducks past it.
        if (source.obstacle?.definition.clearedOnHit) obstacles.clearObstacle(source.obstacle);
        if (source.hazard) this.deps.hazards?.spare(source.hazard);
        this.recoverAfterHit = true;
        return;
      }
    }

    this.die(reason);
  }

  private die(reason: DeathReason): void {
    this.deathReason = reason;
    this.deathTimer = this.deps.config.deathToGameOverDelay;
    this.deps.player.kill();
    this.deps.events.emit('player:died', { reason });
  }
}
